const STICKY_SELECTOR_DESKTOP = '.group-block--sticky-desktop';
const STICKY_SELECTOR_MOBILE = '.group-block--sticky-mobile';
const DESKTOP_BREAKPOINT = 750;

class GroupSticky {
  /** @type {HTMLElement} */
  element;
  /** @type {number} */
  originalTop;
  /** @type {number} */
  originalLeft;
  /** @type {number} */
  originalRight;
  /** @type {number} */
  originalWidth;
  /** @type {number} */
  originalHeight;
  /** @type {HTMLElement | null} */
  placeholder = null;
  /** @type {HTMLElement | null} */
  originalParent = null;
  /** @type {Node | null} */
  originalNextSibling = null;
  /** @type {boolean} */
  isFixed = false;
  /** @type {number | null} */
  rafId = null;
  /** @type {number | null} */
  resizeRafId = null;
  /** @type {number | null} */
  bottomUpdateRafId = null;
  /** @type {() => void} */
  handleScroll;
  /** @type {() => void} */
  handleResize;
  /** @type {boolean} */
  isMobile = false;
  /** @type {'top' | 'bottom'} */
  stickyPosition = 'top';
  /** @type {number | null} */
  lastBottomPosition = null;
  /** @type {boolean | null} */
  lastShouldBeFixed = null;
  /** @type {number} */
  lastScrollY = 0;
  /** @type {number} */
  stateChangeCounter = 0;

  /**
   * Gets the header height from CSS variable, with fallback to actual header element
   * Always gets fresh value to account for mobile/desktop differences
   * @returns {number}
   */
  getHeaderHeight() {
    // First try to get from CSS variable (most reliable, updated by header component)
    const headerHeightVar = getComputedStyle(document.body).getPropertyValue('--header-height');
    if (headerHeightVar) {
      const parsed = parseFloat(headerHeightVar);
      if (!isNaN(parsed) && parsed > 0) {
        return Math.floor(parsed);
      }
    }

    // Fallback: measure the actual header element directly
    // This ensures we get the correct height for current viewport (mobile vs desktop)
    const headerElement = document.querySelector('#header-component') || document.querySelector('header-component');
    if (headerElement instanceof HTMLElement) {
      const rect = headerElement.getBoundingClientRect();
      if (rect.height > 0) {
        return Math.floor(rect.height);
      }
    }

    // Final fallback: check for header-group
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup instanceof HTMLElement) {
      const rect = headerGroup.getBoundingClientRect();
      if (rect.height > 0) {
        return Math.floor(rect.height);
      }
    }

    return 0;
  }

  /**
   * Gets the end target element and its bottom position (where sticky should stop)
   * @returns {{element: HTMLElement | null, bottom: number}}
   */
  getEndTarget() {
    // Check for custom end target selector from data attribute
    const customSelector = this.element.getAttribute('data-sticky-end-target');

    let targetElement = null;

    if (customSelector && customSelector.trim() !== '') {
      // Use custom selector
      try {
        targetElement = document.querySelector(customSelector.trim());
      } catch (e) {
        console.warn('Invalid sticky end target selector:', customSelector);
      }
    }

    // Fallback to main content element if no custom selector or not found
    if (!targetElement) {
      targetElement = document.getElementById('MainContent') || document.querySelector('main');
    }

    if (!targetElement || !(targetElement instanceof HTMLElement)) {
      return { element: null, bottom: Infinity };
    }

    const rect = targetElement.getBoundingClientRect();
    return {
      element: targetElement,
      bottom: rect.bottom + window.scrollY
    };
  }

  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;

    // Determine if this is mobile or desktop sticky
    this.isMobile = element.classList.contains('group-block--sticky-mobile');

    // Get sticky position from data attribute
    const positionAttr = this.isMobile
      ? element.getAttribute('data-sticky-position-mobile')
      : element.getAttribute('data-sticky-position-desktop');
    this.stickyPosition = (positionAttr === 'bottom' ? 'bottom' : 'top');

    // Set z-index and pointer-events immediately to ensure clickability from the start
    this.element.style.setProperty('z-index', '10', 'important');
    this.element.style.setProperty('pointer-events', 'auto', 'important');
    // Ensure child elements are interactive from the start
    this.ensureChildInteractivity();

    const rect = this.element.getBoundingClientRect();
    const parent = this.element.parentElement;
    const parentRect = parent?.getBoundingClientRect();

    // Store section top position (where element will be positioned)
    if (parentRect) {
      this.sectionTop = parentRect.top + window.scrollY;
    } else {
      this.sectionTop = rect.top + window.scrollY;
    }

    this.originalTop = rect.top + window.scrollY;
    this.originalLeft = rect.left;
    this.originalWidth = rect.width;
    this.originalHeight = rect.height;

    // Calculate right position relative to parent
    if (parentRect) {
      this.originalRight = parentRect.right - rect.right;
    } else {
      this.originalRight = window.innerWidth - rect.right;
    }

    this.handleScroll = () => this.update();
    this.handleResize = () => {
      // Use requestAnimationFrame to ensure layout has settled before recalculating
      if (this.resizeRafId !== null) {
        cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.handleResizeInternal();
      });
    };

    this.init();
  }

  init() {
    // Check if sticky should be active for current viewport
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const hasDesktopClass = this.element.classList.contains('group-block--sticky-desktop');
    const hasMobileClass = this.element.classList.contains('group-block--sticky-mobile');

    // Only initialize if element has the appropriate class for current viewport
    if (isDesktop && !hasDesktopClass) return; // Desktop viewport but no desktop sticky class
    if (!isDesktop && !hasMobileClass) return; // Mobile viewport but no mobile sticky class

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    // Position absolutely immediately to avoid delay
    // Try immediate execution first, fallback to RAF if needed
    if (this.element.offsetParent !== null) {
      // Element is already in layout, position immediately
      this.setSticky();
      this.checkPosition();
    } else {
      // Wait for layout
      requestAnimationFrame(() => {
        this.setSticky();
        this.checkPosition();
      });
    }
  }

  handleResizeInternal() {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const hasDesktopClass = this.element.classList.contains('group-block--sticky-desktop');
    const hasMobileClass = this.element.classList.contains('group-block--sticky-mobile');
    const shouldBeActive = (isDesktop && hasDesktopClass) || (!isDesktop && hasMobileClass);

    if (!shouldBeActive) {
      this.cleanup();
      return;
    }

    // Always fully reinitialize on resize to ensure everything is recalculated correctly
    // Clean up current state completely
    const wasFixed = this.isFixed;
    const wasSticky = this.element.hasAttribute('data-sticky-active');

    // Remove event listeners first
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    // Cancel any pending animations
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    if (this.bottomUpdateRafId !== null) {
      cancelAnimationFrame(this.bottomUpdateRafId);
      this.bottomUpdateRafId = null;
    }

    // Store current dimensions and position before resetting (needed to prevent layout shift)
    const currentWidth = this.element.offsetWidth || this.originalWidth || 0;
    const parent = this.element.parentElement;
    let currentRightOffset = 0;
    let parentPaddingRight = 0;

    // Calculate current right position before moving/resetting
    if (parent) {
      const parentStyle = getComputedStyle(parent);
      parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
      const parentRect = parent.getBoundingClientRect();
      const elementRect = this.element.getBoundingClientRect();
      currentRightOffset = parentRect.right - elementRect.right;
    } else {
      currentRightOffset = this.originalRight || 0;
    }

    // Move element back to original parent if it was in body
    if (wasFixed && this.originalParent && this.element.parentElement === document.body) {
      if (this.originalNextSibling && this.originalNextSibling.parentElement === this.originalParent) {
        this.originalParent.insertBefore(this.element, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(this.element);
      }
      // Recalculate right offset after moving back
      const newParent = this.element.parentElement;
      if (newParent) {
        const newParentStyle = getComputedStyle(newParent);
        const newParentPaddingRight = parseFloat(newParentStyle.paddingRight) || 0;
        const newParentRect = newParent.getBoundingClientRect();
        const newElementRect = this.element.getBoundingClientRect();
        currentRightOffset = newParentRect.right - newElementRect.right;
        parentPaddingRight = newParentPaddingRight;
      }
    }

    // Keep placeholder in place to prevent layout shift, but update its width
    // Don't remove it - we'll update it or recreate it during reinitialization
    if (this.placeholder && currentWidth > 0) {
      this.placeholder.style.width = `${currentWidth}px`;
    } else if (!this.placeholder && currentWidth > 0 && wasSticky) {
      // Create placeholder if it doesn't exist but element was sticky
      this.placeholder = document.createElement('div');
      this.placeholder.className = 'group-block-sticky-placeholder';
      this.placeholder.style.height = '0';
      this.placeholder.style.width = `${currentWidth}px`;
      this.placeholder.style.visibility = 'hidden';
      this.placeholder.style.pointerEvents = 'none';
      this.placeholder.style.flexShrink = '0';
      const parent = this.element.parentElement;
      if (parent) {
        parent.insertBefore(this.placeholder, this.element);
      }
    }

    // Remove from fixedElements set if it was there
    if (wasFixed) {
      fixedElements.delete(this.element);
    }

    // Reset all styles and state, but keep element positioned absolutely to prevent layout shift
    // This prevents the parent container from expanding
    this.element.style.position = 'absolute';
    this.element.style.top = '0';
    this.element.style.right = `${currentRightOffset + parentPaddingRight}px`;
    this.element.style.bottom = '';
    this.element.style.width = `${currentWidth}px`;
    this.element.style.zIndex = '';
    this.element.style.removeProperty('--sticky-width');
    this.element.removeAttribute('data-sticky-active');
    this.isFixed = false;

    // Update isMobile based on current viewport
    this.isMobile = !isDesktop && hasMobileClass;

    // Update sticky position based on current viewport
    const positionAttr = this.isMobile
      ? this.element.getAttribute('data-sticky-position-mobile')
      : this.element.getAttribute('data-sticky-position-desktop');
    this.stickyPosition = (positionAttr === 'bottom' ? 'bottom' : 'top');

    // Reset original parent tracking
    this.originalParent = null;
    this.originalNextSibling = null;

    // Force reflow to ensure styles are reset
    void this.element.offsetHeight;

    // Use double requestAnimationFrame plus a small timeout to ensure header height is updated
    // The header component's ResizeObserver might need a moment to update the CSS variable
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Small delay to allow header ResizeObserver to update CSS variable
        setTimeout(() => {
          // Double-check that element should still be active (in case of rapid resize)
          const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
          const hasDesktopClass = this.element.classList.contains('group-block--sticky-desktop');
          const hasMobileClass = this.element.classList.contains('group-block--sticky-mobile');
          const shouldBeActive = (isDesktop && hasDesktopClass) || (!isDesktop && hasMobileClass);

          if (!shouldBeActive) {
            return;
          }

          // Force a fresh header height check to ensure it's updated for current viewport
          // Double-check header height with retry logic to ensure correct value
          let headerHeight = this.getHeaderHeight();

          // Verify header height is reasonable (should be > 0 and < 200px typically)
          // If wrong, measure directly from header element
          if (headerHeight === 0 || headerHeight > 200) {
            // Measure directly from header element
            const headerElement = document.querySelector('#header-component') || document.querySelector('header-component');
            if (headerElement instanceof HTMLElement) {
              const headerRect = headerElement.getBoundingClientRect();
              if (headerRect.height > 0) {
                headerHeight = Math.floor(headerRect.height);
                // Update CSS variable for consistency
                document.body.style.setProperty('--header-height', `${headerHeight}px`);
              } else {
                // Try header-group as fallback
                const headerGroup = document.querySelector('#header-group');
                if (headerGroup instanceof HTMLElement) {
                  const headerGroupRect = headerGroup.getBoundingClientRect();
                  if (headerGroupRect.height > 0) {
                    headerHeight = Math.floor(headerGroupRect.height);
                    document.body.style.setProperty('--header-height', `${headerHeight}px`);
                  }
                }
              }
            }
          }

          // Double-check: if still wrong after direct measurement, wait a bit and try again
          if (headerHeight === 0 || headerHeight > 200) {
            // Wait a bit more for header ResizeObserver to finish
            setTimeout(() => {
              const freshHeaderHeight = this.getHeaderHeight();
              if (freshHeaderHeight > 0 && freshHeaderHeight <= 200) {
                // Got a valid header height, update positioning
                const currentScrollY = window.scrollY || window.pageYOffset;
                if (this.isFixed) {
                  this.element.style.top = `${Math.floor(freshHeaderHeight)}px`;
                }
                // Recheck position with correct header height
                this.checkPosition();
              }
            }, 20);
          }

          // Store header height for this instance to use in positioning
          this.currentHeaderHeight = headerHeight;

          // Recalculate original positions from scratch (like in constructor)
          const rect = this.element.getBoundingClientRect();
          const parent = this.element.parentElement;
          const parentRect = parent?.getBoundingClientRect();

          if (parentRect) {
            this.sectionTop = parentRect.top + window.scrollY;
          } else {
            this.sectionTop = rect.top + window.scrollY;
          }

          this.originalTop = rect.top + window.scrollY;
          this.originalLeft = rect.left;
          this.originalWidth = rect.width;
          this.originalHeight = rect.height;

          if (parentRect) {
            this.originalRight = parentRect.right - rect.right;
          } else {
            this.originalRight = window.innerWidth - rect.right;
          }

          // Ensure z-index and pointer-events are set (like in constructor)
          this.element.style.setProperty('z-index', '10', 'important');
          this.element.style.setProperty('pointer-events', 'auto', 'important');
          this.ensureChildInteractivity();

          // Re-add event listeners (they were removed during cleanup)
          window.addEventListener('scroll', this.handleScroll, { passive: true });
          window.addEventListener('resize', this.handleResize, { passive: true });

          // Ensure element is in clean state before reinitializing
          // Remove data-sticky-active if it exists to force setSticky() to run
          if (this.element.hasAttribute('data-sticky-active')) {
            this.element.removeAttribute('data-sticky-active');
          }

          // Temporarily reset to natural flow to get accurate measurements
          // This is safe because placeholder maintains the space
          const tempPosition = this.element.style.position;
          const tempTop = this.element.style.top;
          const tempRight = this.element.style.right;
          const tempWidth = this.element.style.width;

          this.element.style.position = '';
          this.element.style.top = '';
          this.element.style.right = '';
          this.element.style.width = '';

          // Force reflow to get natural measurements
          void this.element.offsetHeight;

          // Now call setSticky which will get accurate measurements
          this.setSticky();

          // Verify sticky was set, if not force it directly
          if (!this.element.hasAttribute('data-sticky-active')) {
            // Force it by directly setting the required attributes and styles
            const rect = this.element.getBoundingClientRect();
            const width = rect.width;
            const parent = this.element.parentElement;
            let parentPaddingRight = 0;
            let rightOffset = 0;

            if (parent) {
              const parentStyle = getComputedStyle(parent);
              parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
              const parentRect = parent.getBoundingClientRect();
              rightOffset = parentRect.right - rect.right;
            } else {
              rightOffset = this.originalRight;
            }

            // Create or update placeholder
            if (!this.placeholder) {
              this.placeholder = document.createElement('div');
              this.placeholder.className = 'group-block-sticky-placeholder';
              this.placeholder.style.height = '0';
              this.placeholder.style.width = `${width}px`;
              this.placeholder.style.visibility = 'hidden';
              this.placeholder.style.pointerEvents = 'none';
              this.placeholder.style.flexShrink = '0';
              const parent = this.element.parentElement;
              if (parent) {
                parent.insertBefore(this.placeholder, this.element);
              }
            } else {
              this.placeholder.style.width = `${width}px`;
            }

            // Set sticky directly
            this.element.style.setProperty('--sticky-width', `${width}px`);
            this.element.setAttribute('data-sticky-active', '');
            this.element.style.position = 'absolute';
            this.element.style.top = '0';
            this.element.style.right = `${rightOffset + parentPaddingRight}px`;
            this.element.style.width = `${width}px`;
            this.element.style.setProperty('z-index', '10', 'important');
            this.element.style.setProperty('pointer-events', 'auto', 'important');
            this.element.style.overflowY = 'auto';
            this.element.style.overflowX = 'hidden';
            this.ensureChildInteractivity();
          }

          // Always force a position check after reinitialization to ensure correct header height
          // Use another RAF to ensure layout is settled
          requestAnimationFrame(() => {
            // Double-check header height one more time before positioning
            const finalHeaderHeight = this.getHeaderHeight();
            if (finalHeaderHeight > 0 && finalHeaderHeight <= 200 && finalHeaderHeight !== headerHeight) {
              // Header height changed, update stored value
              this.currentHeaderHeight = finalHeaderHeight;
              // Update CSS variable if it's different
              const currentVar = getComputedStyle(document.body).getPropertyValue('--header-height');
              const currentVarHeight = currentVar ? parseFloat(currentVar) : 0;
              if (Math.abs(currentVarHeight - finalHeaderHeight) > 1) {
                document.body.style.setProperty('--header-height', `${finalHeaderHeight}px`);
              }
            }
            this.checkPosition();
          });
        }, 50); // Small delay to allow header ResizeObserver to update
      });
    });
  }

  /**
   * Updates bottom position for bottom sticky elements
   * Batched in requestAnimationFrame to prevent flickering
   * @param {number} viewportHeight
   */
  updateBottomPosition(viewportHeight) {
    if (!this.isFixed || this.stickyPosition !== 'bottom') {
      return;
    }

    // Get actual current height of element (may be limited by max-height)
    const elementRect = this.element.getBoundingClientRect();
    const elementHeight = elementRect.height;

    // Get end target element (selector might be in data attribute)
    const customSelector = this.element.getAttribute('data-sticky-end-target');
    let targetElement = null;

    if (customSelector && customSelector.trim() !== '') {
      try {
        targetElement = document.querySelector(customSelector.trim());
        if (!targetElement) {
          console.warn('[GroupSticky] End target not found:', customSelector);
        }
      } catch (e) {
        console.warn('[GroupSticky] Invalid selector:', customSelector, e);
      }
    }

    // Fallback to main if no custom selector or not found
    if (!targetElement) {
      targetElement = document.getElementById('MainContent') || document.querySelector('main');
    }

    // Bottom positioning: stick to bottom of viewport
    let bottomPosition = 0; // Distance from bottom of viewport

    // If target element exists, check if element would go past target
    if (targetElement && targetElement instanceof HTMLElement) {
      const targetRect = targetElement.getBoundingClientRect();

      // Calculate where element's top would be if positioned at bottom
      const elementTopAtBottom = viewportHeight - elementHeight - bottomPosition;

      // Check if element would go past target
      if (elementTopAtBottom < targetRect.top) {
        // Limit: position element so its top aligns with target's top
        const limitedBottom = viewportHeight - targetRect.top - elementHeight;
        bottomPosition = Math.max(0, limitedBottom);
      }
    }

    // Floor bottom position for consistency
    bottomPosition = Math.floor(bottomPosition);

    // Only update if position actually changed to prevent flickering
    if (this.lastBottomPosition === null || Math.abs(this.lastBottomPosition - bottomPosition) > 0.5) {
      // Set bottom position
      this.element.style.top = '';
      this.element.style.bottom = `${bottomPosition}px`;
      this.element.style.setProperty('z-index', '10', 'important');
      this.lastBottomPosition = bottomPosition;
    }
  }

  update() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.checkPosition();
    });
  }

  checkPosition() {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const hasDesktopClass = this.element.classList.contains('group-block--sticky-desktop');
    const hasMobileClass = this.element.classList.contains('group-block--sticky-mobile');
    const shouldBeActive = (isDesktop && hasDesktopClass) || (!isDesktop && hasMobileClass);
    if (!shouldBeActive) return;

    const scrollY = window.scrollY || window.pageYOffset;
    // Always get fresh header height to account for mobile/desktop differences
    const headerHeight = this.getHeaderHeight();
    const viewportHeight = window.innerHeight;

    // For top position: element becomes fixed when section top reaches header height
    // For bottom position: element becomes fixed when it enters viewport
    let shouldBeFixed;
    if (this.stickyPosition === 'bottom') {
      // Bottom sticky logic: check start and end boundaries

      // Get end target element to check when to stop
      const customSelector = this.element.getAttribute('data-sticky-end-target');
      let targetElement = null;
      if (customSelector && customSelector.trim() !== '') {
        try {
          targetElement = document.querySelector(customSelector.trim());
        } catch (e) {
          console.warn('[GroupSticky] Invalid selector:', customSelector, e);
        }
      }
      if (!targetElement) {
        targetElement = document.getElementById('MainContent') || document.querySelector('main');
      }

      // Check if element should start being sticky (when it enters viewport)
      // For bottom sticky: element should become sticky when it enters viewport
      const elementTopInViewport = this.originalTop - scrollY;
      const elementBottomInViewport = this.originalTop + this.originalHeight - scrollY;

      // Determine scroll direction
      const isScrollingUp = scrollY < this.lastScrollY;

      // For bottom sticky:
      // - Scrolling DOWN: element becomes sticky when its TOP enters viewport (early sticking)
      //   As soon as the element's top enters viewport, it can immediately stick to the bottom
      // - Scrolling UP: element stays sticky until its BOTTOM goes above viewport bottom (proper unsticking)
      let shouldBeSticky;
      if (isScrollingUp) {
        // Scrolling up: element should unstick when its bottom goes above viewport bottom
        // Keep sticky if element bottom is still at or below viewport bottom
        shouldBeSticky = elementBottomInViewport <= viewportHeight;
      } else {
        // Scrolling down: element should stick when it CAN stick to the bottom
        // Element sticks at bottom: 0, so its top would be at viewportHeight - elementHeight
        // Element should become sticky when its natural top is at or above this position
        // This means: elementTopInViewport <= viewportHeight - elementHeight
        const elementHeight = this.originalHeight;
        const stickyTopPosition = viewportHeight - elementHeight;
        shouldBeSticky = elementTopInViewport <= stickyTopPosition;
      }

      // Check if element should stop being sticky (when it reaches end target)
      let shouldStop = false;
      if (targetElement && targetElement instanceof HTMLElement) {
        const targetRect = targetElement.getBoundingClientRect();
        const targetTop = targetRect.top;

        // Get actual element height (may be different from originalHeight if content changed)
        const currentElementHeight = this.element.getBoundingClientRect().height || this.originalHeight;

        // Calculate where sticky element's top would be if positioned at bottom of viewport
        // Bottom sticky elements are positioned at bottom: 0, so top is at viewportHeight - elementHeight
        const stickyElementTopAtBottom = viewportHeight - currentElementHeight;

        // Stop if: target top is at or above where sticky element's top would be
        // This means the sticky element would overlap or go past the target
        shouldStop = targetTop <= stickyElementTopAtBottom;
      }

      // Determine if element should be fixed
      // Must be: should be sticky AND not past end target
      shouldBeFixed = shouldBeSticky && !shouldStop;

      // For bottom sticky, prevent rapid toggling (but allow end target and scroll up to work)
      // Only apply scroll delta protection if NOT stopping due to end target AND NOT scrolling up
      // When scrolling up, allow unsticking to work properly
      if (this.isFixed && !shouldBeFixed && !shouldStop && !isScrollingUp) {
        const scrollDelta = this.lastScrollY - scrollY;
        if (scrollDelta < 150) {
          // Haven't scrolled up enough, keep it fixed (prevents flickering when scrolling down)
          shouldBeFixed = true;
        }
      }
    } else {
      // For top, use original behavior
      shouldBeFixed = scrollY >= this.sectionTop - headerHeight;
    }

    // Store scroll position for next check
    this.lastScrollY = scrollY;

    // Update cache
    this.lastShouldBeFixed = shouldBeFixed;

    const isSticky = this.element.hasAttribute('data-sticky-active');

    // Element should always be sticky (absolute) when active
    // Only switch between absolute and fixed based on scroll
    if (!isSticky) {
      this.setSticky();
    }

    if (shouldBeFixed) {
      // Should be fixed to viewport
      if (!this.isFixed) {
        this.setFixed();
      } else {
        // Already fixed, but ensure interactivity is maintained
        this.ensureChildInteractivity();
      }

      // For bottom sticky, batch position updates to prevent flickering
      // Only update position if element is actually fixed
      if (this.isFixed && this.stickyPosition === 'bottom') {
        // Throttle position updates - only update every 3rd frame to prevent flickering
        // Cancel any pending update
        if (this.bottomUpdateRafId !== null && this.bottomUpdateRafId !== undefined) {
          cancelAnimationFrame(this.bottomUpdateRafId);
        }

        // Use requestAnimationFrame to batch position updates
        // This prevents rapid DOM updates that cause flickering
        this.bottomUpdateRafId = requestAnimationFrame(() => {
          this.bottomUpdateRafId = requestAnimationFrame(() => {
            this.bottomUpdateRafId = requestAnimationFrame(() => {
              this.bottomUpdateRafId = null;
              // Only update if still fixed and bottom sticky
              if (this.isFixed && this.stickyPosition === 'bottom') {
                this.updateBottomPosition(window.innerHeight);
              }
            });
          });
        });
        // Skip the rest of the position update logic for bottom sticky
        // The updateBottomPosition method handles all bottom positioning
        return;
      }

      // Get actual current height of element (may be limited by max-height)
      const elementRect = this.element.getBoundingClientRect();
      const elementHeight = elementRect.height;

      // Get end target element (selector might be in data attribute)
      const customSelector = this.element.getAttribute('data-sticky-end-target');
      let targetElement = null;

      if (customSelector && customSelector.trim() !== '') {
        try {
          targetElement = document.querySelector(customSelector.trim());
          if (!targetElement) {
            console.warn('[GroupSticky] End target not found:', customSelector);
          }
        } catch (e) {
          console.warn('[GroupSticky] Invalid selector:', customSelector, e);
        }
      }

      // Fallback to main if no custom selector or not found
      if (!targetElement) {
        targetElement = document.getElementById('MainContent') || document.querySelector('main');
      }

      // Top positioning logic continues below
      if (this.stickyPosition !== 'bottom') {
        // Top positioning: original behavior
        // If target element exists, check if bottom hits target bottom
        if (targetElement && targetElement instanceof HTMLElement) {
          // Get fresh target position on each check (it changes as we scroll)
          const targetRect = targetElement.getBoundingClientRect();
          // Target bottom in viewport coordinates
          const targetBottomInViewport = targetRect.bottom;

          // Get current element position
          const currentTop = Math.floor(parseFloat(this.element.style.top) || headerHeight);
          const currentElementBottom = currentTop + elementHeight;

          // Calculate where element's bottom would be if positioned at headerHeight
          const elementBottomAtHeaderHeight = headerHeight + elementHeight;

          // Check if element's bottom would go past target bottom
          // We need to limit if either:
          // 1. Current position would go past target, OR
          // 2. Positioning at headerHeight would go past target
          if (currentElementBottom > targetBottomInViewport || elementBottomAtHeaderHeight > targetBottomInViewport) {
            // Calculate top position so element's bottom aligns with target's bottom
            // Floor for tight fit
            const limitedTop = Math.floor(targetBottomInViewport - elementHeight);

            // Always use limitedTop to stop at target
            this.element.style.top = `${limitedTop}px`;
            this.element.style.bottom = '';

            // Set z-index to 1 to stay below header menu
            this.element.style.setProperty('z-index', '10', 'important');
          } else {
            // Normal fixed position below header (element won't go past target)
            // Floor headerHeight for tight fit
            this.element.style.top = `${Math.floor(headerHeight)}px`;
            this.element.style.bottom = '';
            // Set z-index to 1 to stay below header menu
            this.element.style.setProperty('z-index', '10', 'important');
          }
        } else {
          // No target found, use normal fixed position
          // Floor headerHeight for tight fit
          this.element.style.top = `${Math.floor(headerHeight)}px`;
          this.element.style.bottom = '';
        }
      }
    } else {
      // Should be absolute (sticky but not fixed)
      // Don't update position - let it scroll naturally with the page
      if (this.isFixed) {
        this.unsetFixed();
        // Reset bottom position tracking when unsetting fixed
        this.lastBottomPosition = null;
      }
    }
  }

  setSticky() {
    // Skip if already sticky
    if (this.element.hasAttribute('data-sticky-active')) return;

    const rect = this.element.getBoundingClientRect();
    const width = rect.width;
    const parent = this.element.parentElement;

    // Get parent padding and calculate right position relative to parent
    let parentPaddingRight = 0;
    let rightOffset = 0;

    if (parent) {
      const parentStyle = getComputedStyle(parent);
      parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
      const parentRect = parent.getBoundingClientRect();
      // Calculate right offset from parent's right edge
      rightOffset = parentRect.right - rect.right;
    } else {
      rightOffset = this.originalRight;
    }

    // Create or update zero-height placeholder to remove element from flow
    // Insert it before the element so it takes its place in the flow
    if (!this.placeholder) {
      this.placeholder = document.createElement('div');
      this.placeholder.className = 'group-block-sticky-placeholder';
      this.placeholder.style.height = '0';
      this.placeholder.style.width = `${width}px`;
      this.placeholder.style.visibility = 'hidden';
      this.placeholder.style.pointerEvents = 'none';
      this.placeholder.style.flexShrink = '0';
      this.element.parentNode?.insertBefore(this.placeholder, this.element);
    } else {
      // Update existing placeholder width
      this.placeholder.style.width = `${width}px`;
      // Ensure placeholder is in the right place (before element)
      if (this.placeholder.nextSibling !== this.element) {
        const parent = this.element.parentElement;
        if (parent) {
          parent.insertBefore(this.placeholder, this.element);
        }
      }
    }

    // Position at top of parent section
    this.element.style.setProperty('--sticky-width', `${width}px`);
    this.element.setAttribute('data-sticky-active', '');
    this.element.style.position = 'absolute';
    // Position at top of section (top: 0 relative to parent)
    this.element.style.top = '0';
    this.element.style.right = `${rightOffset + parentPaddingRight}px`;
    this.element.style.width = `${width}px`;
    this.element.style.setProperty('z-index', '10', 'important');
    this.element.style.setProperty('pointer-events', 'auto', 'important');
    this.element.style.isolation = 'isolate';
    // Ensure overflow is set correctly for scrollability
    this.element.style.overflowY = 'auto';
    this.element.style.overflowX = 'hidden';
    // Ensure all child elements remain clickable and scrollable
    this.ensureChildInteractivity();
  }

  unsetSticky() {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const hasDesktopClass = this.element.classList.contains('group-block--sticky-desktop');
    const hasMobileClass = this.element.classList.contains('group-block--sticky-mobile');
    const shouldBeActive = (isDesktop && hasDesktopClass) || (!isDesktop && hasMobileClass);

    // Only unset sticky if it shouldn't be active
    if (shouldBeActive) return;

    this.element.style.position = '';
    this.element.style.top = '';
    this.element.style.bottom = '';
    this.element.style.left = '';
    this.element.style.width = '';
    this.element.style.right = '';
    this.element.style.removeProperty('--sticky-width');
    this.element.removeAttribute('data-sticky-active');

    // Remove placeholder
    if (this.placeholder) {
      this.placeholder.remove();
      this.placeholder = null;
    }
  }

  setFixed() {
    this.isFixed = true;
    const rect = this.element.getBoundingClientRect();
    const width = rect.width;
    // Always get fresh header height right before setting fixed position
    // This ensures correct height for current viewport (mobile vs desktop)
    const headerHeight = this.getHeaderHeight();

    // Store original values if not already stored
    if (!this.originalWidth) {
      this.originalWidth = width;
    }
    if (!this.originalHeight) {
      this.originalHeight = rect.height;
    }

    // Get parent padding and calculate right position relative to viewport
    const parent = this.element.parentElement;
    let rightPosition = 0;

    if (parent) {
      const parentStyle = getComputedStyle(parent);
      const parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
      const parentRect = parent.getBoundingClientRect();
      // Calculate right position from viewport edge
      rightPosition = window.innerWidth - rect.right + parentPaddingRight;
    } else {
      // No parent, use original right calculation
      rightPosition = this.originalRight;
    }

    // Move element to body to escape parent stacking contexts
    if (!this.originalParent) {
      this.originalParent = this.element.parentElement;
      this.originalNextSibling = this.element.nextSibling;
    }

    // Move to body if not already there
    if (this.element.parentElement !== document.body) {
      // Find the correct position in body based on document order
      const allStickyElements = Array.from(document.querySelectorAll(`${STICKY_SELECTOR_DESKTOP}, ${STICKY_SELECTOR_MOBILE}`));
      const currentIndex = allStickyElements.indexOf(this.element);

      // Find the next sticky element that's already in body to insert before it
      let insertBefore = null;
      for (let i = currentIndex + 1; i < allStickyElements.length; i++) {
        const nextElement = allStickyElements[i];
        if (nextElement instanceof HTMLElement && nextElement.parentElement === document.body) {
          insertBefore = nextElement;
          break;
        }
      }

      if (insertBefore) {
        document.body.insertBefore(this.element, insertBefore);
      } else {
        document.body.appendChild(this.element);
      }
    }

    // Add to fixed elements set
    fixedElements.add(this.element);

    // Switch from absolute to fixed positioning
    // Maintain right position accounting for padding
    this.element.style.position = 'fixed';

    // Set initial position based on sticky position (will be updated in checkPosition)
    if (this.stickyPosition === 'bottom') {
      this.element.style.top = '';
      this.element.style.bottom = '0px';
      // Initialize bottom position tracking
      this.lastBottomPosition = 0;
    } else {
      // Floor headerHeight for tight fit
      this.element.style.top = `${Math.floor(headerHeight)}px`;
      this.element.style.bottom = '';
      // Reset bottom position tracking for top positioning
      this.lastBottomPosition = null;
    }

    this.element.style.right = `${rightPosition}px`;
    this.element.style.width = `${width}px`;
    this.element.style.setProperty('z-index', '10', 'important');
    this.element.style.setProperty('pointer-events', 'auto', 'important');
    // Ensure overflow is set correctly for scrollability
    this.element.style.overflowY = 'auto';
    this.element.style.overflowX = 'hidden';
    // Ensure all child elements remain clickable and scrollable
    this.ensureChildInteractivity();
  }

  unsetFixed() {
    this.isFixed = false;

    // Remove from fixed elements set
    fixedElements.delete(this.element);

    // Move element back to original parent if it was moved to body
    if (this.originalParent && this.element.parentElement === document.body) {
      if (this.originalNextSibling && this.originalNextSibling.parentElement === this.originalParent) {
        this.originalParent.insertBefore(this.element, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(this.element);
      }
    }

    // Update z-index of remaining fixed elements to maintain proper stacking
    fixedElements.forEach((fixedElement) => {
      // No z-index needed - rely on document order
    });

    // Switch back to absolute positioning (still sticky, just not fixed)
    if (this.element.hasAttribute('data-sticky-active')) {
      const width = this.originalWidth;
      const parent = this.element.parentElement;

      // Get parent padding and calculate right position
      let parentPaddingRight = 0;
      let rightOffset = 0;

      if (parent) {
        const parentStyle = getComputedStyle(parent);
        parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
        // Recalculate right position relative to parent
        const parentRect = parent.getBoundingClientRect();
        const elementRect = this.element.getBoundingClientRect();
        rightOffset = parentRect.right - elementRect.right;
      } else {
        rightOffset = this.originalRight;
      }

      this.element.style.position = 'absolute';
      // Position at top of section
      this.element.style.top = '0';
      this.element.style.bottom = '';
      this.element.style.right = `${rightOffset + parentPaddingRight}px`;
      this.element.style.width = `${width}px`;
      this.element.style.zIndex = '';
      // Ensure overflow is set correctly for scrollability
      this.element.style.overflowY = 'auto';
      this.element.style.overflowX = 'hidden';
      // Ensure all child elements remain clickable and scrollable
      this.ensureChildInteractivity();
    }
  }

  /**
   * Ensures all child elements within the sticky element remain clickable and scrollable
   */
  ensureChildInteractivity() {
    // Ensure the element itself is always clickable and scrollable
    this.element.style.setProperty('pointer-events', 'auto', 'important');
    this.element.style.overflowY = 'auto';
    this.element.style.overflowX = 'hidden';

    // Find all interactive elements and ensure they remain clickable
    const interactiveSelectors = 'a, button, input, textarea, select, [role="button"], [tabindex], [onclick]';
    const interactiveElements = this.element.querySelectorAll(interactiveSelectors);

    interactiveElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        // Ensure interactive elements have pointer-events auto
        const computedStyle = getComputedStyle(el);
        if (computedStyle.pointerEvents === 'none') {
          el.style.pointerEvents = 'auto';
        }
      }
    });

    // Find all scrollable containers within the sticky element
    const scrollableContainers = this.element.querySelectorAll('[style*="overflow"], .group-block-content');
    scrollableContainers.forEach((container) => {
      if (container instanceof HTMLElement && container !== this.element) {
        // Preserve existing overflow settings but ensure scrollability is maintained
        const computedStyle = getComputedStyle(container);
        // Only override if overflow is completely hidden and content might overflow
        if (computedStyle.overflowY === 'hidden') {
          // Check if content might overflow
          if (container.scrollHeight > container.clientHeight) {
            container.style.overflowY = 'auto';
          }
        }
      }
    });
  }

  cleanup() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }

    if (this.bottomUpdateRafId !== null) {
      cancelAnimationFrame(this.bottomUpdateRafId);
      this.bottomUpdateRafId = null;
    }

    this.unsetFixed();
    this.unsetSticky();

    // Clean up placeholder
    if (this.placeholder) {
      this.placeholder.remove();
      this.placeholder = null;
    }
  }
}

const instances = new WeakMap();
const fixedElements = new Set();

/**
 * Gets the z-index for a fixed sticky element based on its position in the document
 * @param {HTMLElement} element
 * @returns {number}
 */
function getZIndexForFixedElement(element) {
  const allStickyElements = Array.from(document.querySelectorAll(`${STICKY_SELECTOR_DESKTOP}, ${STICKY_SELECTOR_MOBILE}`));
  const index = allStickyElements.indexOf(element);
  // Base z-index below header menu (11), increment by 1 for each subsequent sticky element
  // Header menu is at z-index 12 (--layer-header-menu), so we use 11 to stay below it
  return 11 + index;
}

/**
 * @param {ParentNode} [context=document]
 */
function initSticky(context = document) {
  const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

  // Get both desktop and mobile sticky elements
  const desktopStickyElements = context.querySelectorAll(STICKY_SELECTOR_DESKTOP);
  const mobileStickyElements = context.querySelectorAll(STICKY_SELECTOR_MOBILE);
  // Combine and deduplicate (elements with both classes will appear in both arrays)
  const stickyElementsSet = new Set([...desktopStickyElements, ...mobileStickyElements]);
  const stickyElements = Array.from(stickyElementsSet);

  stickyElements.forEach((element) => {
    if (instances.has(element)) {
      // If instance exists, reinitialize it (for customizer setting changes)
      const instance = instances.get(element);
      instance?.cleanup();
      instances.delete(element);
    }

    if (!(element instanceof HTMLElement)) return;

    // Check if element actually has a sticky class
    const isDesktopSticky = element.classList.contains('group-block--sticky-desktop');
    const isMobileSticky = element.classList.contains('group-block--sticky-mobile');

    if (!isDesktopSticky && !isMobileSticky) return;

    // Initialize if viewport matches sticky type
    if ((isDesktopSticky && isDesktop) || (isMobileSticky && !isDesktop)) {
      const instance = new GroupSticky(element);
      instances.set(element, instance);
    }
  });
}

/**
 * @param {ParentNode} [context=document]
 */
function destroySticky(context = document) {
  // Get both desktop and mobile sticky elements
  const desktopStickyElements = context.querySelectorAll(STICKY_SELECTOR_DESKTOP);
  const mobileStickyElements = context.querySelectorAll(STICKY_SELECTOR_MOBILE);
  // Combine and deduplicate (elements with both classes will appear in both arrays)
  const stickyElementsSet = new Set([...desktopStickyElements, ...mobileStickyElements]);
  const stickyElements = Array.from(stickyElementsSet);

  stickyElements.forEach((element) => {
    const instance = instances.get(element);
    instance?.cleanup();
    instances.delete(element);
  });
}

// Global resize handler to ensure all sticky elements reinitialize on viewport changes
/** @type {number | null} */
let globalResizeTimeout = null;
window.addEventListener('resize', () => {
  // Debounce global resize to avoid excessive calls
  if (globalResizeTimeout !== null) {
    clearTimeout(globalResizeTimeout);
  }
  globalResizeTimeout = setTimeout(() => {
    // Force all existing instances to reinitialize, and initialize any missing ones
    const allStickyElements = Array.from(document.querySelectorAll(`${STICKY_SELECTOR_DESKTOP}, ${STICKY_SELECTOR_MOBILE}`));
    allStickyElements.forEach((element) => {
      if (instances.has(element)) {
        const instance = instances.get(element);
        // Trigger resize handler for each instance
        if (instance && typeof instance.handleResizeInternal === 'function') {
          instance.handleResizeInternal();
        }
      } else {
        // If element should be sticky but doesn't have an instance, initialize it
        const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
        const hasDesktopClass = element.classList.contains('group-block--sticky-desktop');
        const hasMobileClass = element.classList.contains('group-block--sticky-mobile');
        const shouldBeActive = (isDesktop && hasDesktopClass) || (!isDesktop && hasMobileClass);

        if (shouldBeActive && element instanceof HTMLElement) {
          // Initialize this element
          const instance = new GroupSticky(element);
          instances.set(element, instance);
        }
      }
    });
  }, 100);
}, { passive: true });

document.addEventListener('DOMContentLoaded', () => initSticky());

document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  // Use requestAnimationFrame to ensure DOM is fully updated in customizer
  requestAnimationFrame(() => {
    setTimeout(() => {
      initSticky(target);
    }, 100);
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  destroySticky(target);
});

document.addEventListener('shopify:block:load', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  // Use requestAnimationFrame to ensure DOM is fully updated in customizer
  requestAnimationFrame(() => {
    setTimeout(() => {
      initSticky(target);
    }, 100);
  });
});

document.addEventListener('shopify:block:unload', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  destroySticky(target);
});

// Handle section re-renders in customizer (when settings change)
document.addEventListener('shopify:section:reorder', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  destroySticky(target);
  requestAnimationFrame(() => {
    setTimeout(() => {
      initSticky(target);
    }, 100);
  });
});

// Also reinitialize when section is selected/deselected in customizer
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    const target = /** @type {ParentNode} */ (event.target);
    requestAnimationFrame(() => {
      setTimeout(() => {
        initSticky(target);
      }, 100);
    });
  });
}


